import { element } from 'prop-types';
import React, { createContext, useMemo } from 'react';
import { INITIAL_CUSTOMER_INFO } from '../../constants/booking';
import useSessionStorage from '../../utils/useSessionStorage';

type RequestLocation = {
  line_1: string;
  line_2: string;
  city: string;
  state: string;
  country: string;
  marketCode: string;
  postal_code: string;
  property_type: string;
  propertyInfo: Record<string, any>;
  timezone?: string;
};

type Customer = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
};

type Inventory = {
  rooms: Record<string, number>;
  additionalItems: Record<string, number>;
};

type CustomerRequest = {
  customer: Customer;
  locations: RequestLocation[];
  date: string;
  time: number;
  truck: boolean;
  inventory: Inventory;
  gbIdentity: string;
  inventoryModalAcknowledged: boolean;
  leadId: string;
  leadRecordType?: string;
  affiliateAccountId: string;
  selectedTemplate: string;
  marketingSource?: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    clickid?: string;
    gclid?: string;
    msclkid?: string;
  };
  promo: string;
  metadata: Record<string, any>;
};

type CustomerContext = {
  customerInfo: CustomerRequest;
  setCustomerInfo: React.Dispatch<CustomerRequest>;
};

export const CustomerRequestContext = createContext<CustomerContext>({
  customerInfo: INITIAL_CUSTOMER_INFO,
  setCustomerInfo: null,
});

// Goal of this provider is to always hold the initial information the customer entered
// ie. requested services, basic information, locations - no matter what the quote
// We always want to retain this data for logic and determination
const CustomerRequestProvider = ({ children }) => {
  const [savedCustomerInfo, setSavedCustomerInfo] = useSessionStorage<CustomerRequest>(
    'customerInfo',
    INITIAL_CUSTOMER_INFO,
  );

  const data = useMemo(
    () => ({ customerInfo: savedCustomerInfo, setCustomerInfo: setSavedCustomerInfo }),
    [savedCustomerInfo],
  );

  return <CustomerRequestContext.Provider value={data}>{children}</CustomerRequestContext.Provider>;
};

CustomerRequestProvider.propTypes = {
  children: element,
};

CustomerRequestProvider.defaultProps = {
  children: null,
};

export default CustomerRequestProvider;
